import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import {
  clearAttribution,
  clearReferralCode,
  getAttributionPayload,
  getReferralCode,
} from '../../utils/attribution';
import logger from '../../utils/logger';
import { trackTrialSignup } from '../../utils/analytics';

const LEGAL_DOCUMENTS_VERSION = '2026-03-20';

const initialRegister = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  offer_accepted: false,
  privacy_policy_acknowledged: false,
  marketing_consent: false,
  promo_code: '',
  legal_documents_version: LEGAL_DOCUMENTS_VERSION,
};

const FIELD_LABELS = {
  email: 'Email',
  password: 'Пароль',
  phone: 'Телефон',
  first_name: 'Имя',
  last_name: 'Фамилия',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function translateFieldError({ field, message }) {
  const label = FIELD_LABELS[field] || 'Поле';
  if (/at least 8 characters/i.test(message)) {
    return `${label}: минимум 8 символов.`;
  }
  if (/valid email address/i.test(message)) {
    return `${label}: некорректный формат email.`;
  }
  if (/field required/i.test(message)) {
    return `${label}: обязательно для заполнения.`;
  }
  return `${label}: ${message}`;
}

function translateRegisterError(error) {
  if (error?.fieldErrors?.length) {
    return error.fieldErrors.map(translateFieldError).join(' ');
  }
  const msg = error?.message || '';
  if (/already exists/i.test(msg)) {
    return 'Пользователь с таким email уже зарегистрирован.';
  }
  if (/офер|privacy|offer acceptance/i.test(msg)) {
    return 'Подтвердите условия оферты и политики конфиденциальности.';
  }
  if (/промокод|promo/i.test(msg)) {
    return 'Недействительный промокод.';
  }
  return msg || 'Не удалось зарегистрироваться. Проверьте данные формы.';
}

function RegisterForm({ promoCode = '' }) {
  const { request, showStatus, setStatus } = useApi();
  const { login, setUser } = useAuth();
  const [form, setForm] = useState(() =>
    promoCode ? { ...initialRegister, promo_code: promoCode } : initialRegister
  );
  const [busy, setBusy] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const requiredConsentAccepted = form.offer_accepted && form.privacy_policy_acknowledged;

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!form.email.trim()) {
      return 'Введите email.';
    }
    if (!EMAIL_PATTERN.test(form.email.trim())) {
      return 'Введите email в корректном формате (например, name@example.com).';
    }
    if (!form.password || form.password.length < 8) {
      return 'Пароль должен содержать минимум 8 символов.';
    }
    if (!requiredConsentAccepted) {
      return 'Подтвердите согласие с офертой и политикой конфиденциальности.';
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      showStatus('error', validationError);
      return;
    }

    setBusy(true);
    setStatus(null);

    try {
      await request('/api/v1/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          referral_code: getReferralCode() || null,
          attribution: getAttributionPayload(),
        }),
      });
      clearAttribution();
      clearReferralCode();

      const tokenResponse = await request('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: form.email,
          password: form.password,
        }),
      });
      login(tokenResponse);

      try {
        const userData = await request('/api/v1/auth/me');
        if (userData) {
          setUser(userData);
        }
      } catch (err) {
        logger.warn('Could not load user data after registration:', err);
      }

      trackTrialSignup({
        source: 'register_form',
        marketing_consent: form.marketing_consent,
      });
      setForm(initialRegister);
      showStatus('success', 'Регистрация завершена. Подтвердите email по ссылке из письма.');
    } catch (error) {
      logger.error(error);
      showStatus('error', translateRegisterError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel__form" onSubmit={handleSubmit} noValidate>
      <div className="panel__grid">
        <label>
          Имя
          <input type="text" value={form.first_name} onChange={handleChange('first_name')} />
        </label>

        <label>
          Фамилия
          <input type="text" value={form.last_name} onChange={handleChange('last_name')} />
        </label>
      </div>

      <label>
        Email
        <input type="email" value={form.email} onChange={handleChange('email')} required />
      </label>

      <label>
        Телефон
        <input type="tel" value={form.phone} onChange={handleChange('phone')} />
      </label>

      <label>
        Промокод
        <input
          type="text"
          value={form.promo_code || ''}
          onChange={handleChange('promo_code')}
          placeholder="если есть"
        />
      </label>

      <label>
        Пароль
        <span className="field-hint">Минимум 8 символов.</span>
        <div className="input-with-toggle">
          <input
            type={passwordVisible ? 'text' : 'password'}
            value={form.password}
            onChange={handleChange('password')}
            autoComplete="new-password"
            required
            minLength={8}
          />
          <button
            type="button"
            className="toggle"
            onClick={() => setPasswordVisible((prev) => !prev)}
            aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
          >
            {passwordVisible ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        </div>
      </label>

      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={requiredConsentAccepted}
          onChange={(event) => {
            const checked = event.target.checked;
            setForm((prev) => ({
              ...prev,
              offer_accepted: checked,
              privacy_policy_acknowledged: checked,
            }));
          }}
          required
        />
        <span>
          Я принимаю условия <Link to="/offer" target="_blank" rel="noreferrer">Публичной оферты</Link> и
          подтверждаю ознакомление с <Link to="/privacy" target="_blank" rel="noreferrer">Политикой конфиденциальности</Link>.
        </span>
      </label>

      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={form.marketing_consent}
          onChange={handleChange('marketing_consent')}
        />
        <span>Согласен(а) на получение рекламных и маркетинговых сообщений.</span>
      </label>

      <button type="submit" disabled={busy || !requiredConsentAccepted}>
        Создать аккаунт
      </button>
    </form>
  );
}

export default RegisterForm;
